import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import {
  FaTv,
  FaYoutube,
  FaSave,
  FaCheckCircle,
  FaInfoCircle,
  FaPlay,
  FaQuoteRight,
  FaHeading,
  FaTag
} from "react-icons/fa";
import "../styles/ManageLive.css";

const ManageLive = () => {
  const API_URL = API_ENDPOINTS.LIVE_DARSHAN;

  /* ================= STATE ================= */
  const [liveData, setLiveData] = useState({
    title: "",
    subtitle: "",
    youtubeLink: "",
    liveBadge: "LIVE",
    autoplay: true,
    mute: true,
  });

  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  /* ================= FETCH ================= */
  useEffect(() => {
    fetchLiveData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLiveData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_URL);

      if (res.data) {
        setLiveData({
          title: res.data.title || "",
          subtitle: res.data.subtitle || "",
          youtubeLink: res.data.youtubeLink || "",
          liveBadge: res.data.liveBadge || "LIVE",
          autoplay: res.data.autoplay ?? true,
          mute: res.data.mute ?? true,
        });
      }
    } catch (error) {
      console.log("FETCH ERROR:", error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  /* ================= UPDATE FIELD ================= */
  const updateField = (field, value) => {
    setLiveData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /* ================= YOUTUBE FIX ================= */
  const getYoutubeEmbedUrl = (url) => {
    if (!url) return "";

    // already embed
    if (url.includes("embed/")) return url;

    // youtu.be
    if (url.includes("youtu.be/")) {
      const id = url.split("youtu.be/")[1].split("?")[0];
      return `https://www.youtube.com/embed/${id}`;
    }

    // watch?v=
    if (url.includes("watch?v=")) {
      const id = url.split("watch?v=")[1].split("&")[0];
      return `https://www.youtube.com/embed/${id}`;
    }

    // live stream (channel)
    if (url.includes("live_stream")) return url;

    return url;
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    try {
      setLoading(true);
      setSaveSuccess(false);

      const payload = {
        ...liveData,
        youtubeLink: getYoutubeEmbedUrl(liveData.youtubeLink),
      };

      await axios.post(API_URL, payload);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      alert("🎉 Live Darshan Updated Successfully!");
      fetchLiveData();
    } catch (error) {
      console.log("SAVE ERROR:", error.response?.data || error.message);
      alert("❌ Failed To Save Live Darshan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="manage-live-container">
      {/* BANNER SECTION */}
      <div className="live-banner">
        <div className="banner-content">
          <h1>📺 Manage Live Darshan</h1>
          <p>Update live broadcast streams, autoplay triggers, YouTube live integration URLs, and custom badges.</p>
        </div>
        <div className="banner-stats">
          <div className="stat-pill">
            <FaTv className="icon" />
            <span>Live Broadcast Panel</span>
          </div>
        </div>
      </div>

      {/* 2-COLUMN DASHBOARD WORKSPACE */}
      <div className="live-workspace-layout">
        
        {/* LEFT COLUMN: FORM SETTINGS */}
        <div className="settings-panel-card">
          <div className="panel-card-header">
            <h3><FaInfoCircle className="title-icon" /> Live Stream Parameters</h3>
            <p>Customize details of the live broadcast displayed on the web page.</p>
          </div>

          <div className="panel-card-body">
            <div className="form-group-compact">
              <label><FaHeading className="label-icon" /> Broadcast Title</label>
              <input
                type="text"
                value={liveData.title}
                onChange={(e) => updateField("title", e.target.value)}
                placeholder="e.g. Mandir Live Darshan"
              />
            </div>

            <div className="form-group-compact">
              <label><FaQuoteRight className="label-icon" /> Subtitle / Sacred Mantra</label>
              <textarea
                value={liveData.subtitle}
                onChange={(e) => updateField("subtitle", e.target.value)}
                placeholder="e.g. Ananta Koti Brahmanda Nayaka Rajadhiraja Yogiraja..."
                rows={3}
              />
            </div>

            <div className="form-group-compact">
              <label><FaYoutube className="label-icon" style={{ color: "#ef4444" }} /> YouTube Video / Live Stream URL</label>
              <input
                type="text"
                value={liveData.youtubeLink}
                onChange={(e) => updateField("youtubeLink", e.target.value)}
                placeholder="Paste share link, e.g. https://youtu.be/..."
              />
            </div>

            <div className="form-group-compact">
              <label><FaTag className="label-icon" /> Custom Live Status Badge</label>
              <input
                type="text"
                value={liveData.liveBadge}
                onChange={(e) => updateField("liveBadge", e.target.value)}
                placeholder="e.g. LIVE"
              />
            </div>

            {/* SWITCH TOGGLES */}
            <div className="toggle-inputs-box">
              <div className="toggle-item-row">
                <div className="toggle-info">
                  <span className="toggle-label">Autoplay Stream</span>
                  <span className="toggle-desc">Automatically play when visitors open the page</span>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={liveData.autoplay}
                    onChange={(e) => updateField("autoplay", e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>

              <div className="toggle-item-row">
                <div className="toggle-info">
                  <span className="toggle-label">Mute Audio</span>
                  <span className="toggle-desc">Mute the player audio by default on page load</span>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={liveData.mute}
                    onChange={(e) => updateField("mute", e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: REAL-TIME PREVIEW CANVAS */}
        <div className="preview-panel-card">
          <div className="panel-card-header">
            <h3><FaPlay className="title-icon" /> Live Screen Preview</h3>
            <p>Renders live changes of how it looks on the website.</p>
          </div>

          <div className="preview-canvas">
            <div className="live-screen-mock">
              <div className="screen-header">
                <h4>{liveData.title || "Mandir Live Darshan"}</h4>
                <p>{liveData.subtitle || "Auspicious blessings and daily aartis."}</p>
              </div>

              <div className="screen-video-holder">
                <div className="live-status-pill">
                  <span className="live-dot-pulse"></span>
                  {liveData.liveBadge || "LIVE"}
                </div>

                {liveData.youtubeLink ? (
                  <iframe
                    src={getYoutubeEmbedUrl(liveData.youtubeLink)}
                    title="Live Preview Frame"
                    frameBorder="0"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    className="live-preview-iframe"
                  ></iframe>
                ) : (
                  <div className="no-stream-placeholder">
                    <FaYoutube className="icon" />
                    <span>Enter live stream link to preview</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FLOATING SAVE BAR */}
      <div className="floating-save-bar">
        <div className="save-bar-info">
          <span>Unsaved changes to live darshan settings will be lost.</span>
        </div>
        
        <button 
          className={`action-btn save-btn-primary ${loading ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="spinner-small"></div> Saving...
            </>
          ) : saveSuccess ? (
            <>
              <FaCheckCircle /> Saved Successfully!
            </>
          ) : (
            <>
              <FaSave /> Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ManageLive;
